/**
 * Shared dependencies
 */
import { CUSTOM_DATA_EVENTS } from '../shared/constants/events';
import type {
	CreateListPayload,
	CreateListCallbackEvent,
	DeleteListPayload,
	DeleteListCallbackEvent,
	EditListPayload,
	EditListCallbackEvent,
	FetchListsPayload,
	FetchListsCallbackEvent,
	SelectListPayload,
	SelectListCallbackEvent,
} from '../shared/types/lists';

/**
 * Internal dependencies
 */
import { BaseEvent } from './BaseEvent';

/**
 * Event handler for recipe list-specific events.
 * Extends the BaseEvent to provide methods for
 * listening to and dispatching recipe list-related events.
 */
export class ListEvent extends BaseEvent {
	/**
	 * Listens for the create list event.
	 * @param {CreateListCallbackEvent} callback - Function to handle the event. Receives the full CustomEvent.
	 * @return {() => void} A function to unsubscribe the listener.
	 */
	onCreate(callback: CreateListCallbackEvent): () => void {
		return this.listen(CUSTOM_DATA_EVENTS.RECIPE_LIST_CREATED, callback);
	}

	/**
	 * Dispatches the create list event.
	 * @param {CreateListPayload} payload - The data to send with the event.
	 */
	create(payload: CreateListPayload) {
		this.dispatch(CUSTOM_DATA_EVENTS.RECIPE_LIST_CREATED, payload);
	}

	/**
	 * Listens for the delete list event.
	 * @param {DeleteListCallbackEvent} callback - Function to handle the event. Receives the full CustomEvent.
	 * @return {() => void} A function to unsubscribe the listener.
	 */
	onDelete(callback: DeleteListCallbackEvent): () => void {
		return this.listen(CUSTOM_DATA_EVENTS.RECIPE_LIST_DELETED, callback);
	}

	/**
	 * Dispatches the delete list event.
	 * @param {DeleteListPayload} payload - The data to send with the event.
	 */
	delete(payload: DeleteListPayload) {
		this.dispatch(CUSTOM_DATA_EVENTS.RECIPE_LIST_DELETED, payload);
	}

	/**
	 * Listens for the edit list event.
	 * @param {EditListCallbackEvent} callback - Function to handle the event. Receives the full CustomEvent.
	 * @return {() => void} A function to unsubscribe the listener.
	 */
	onEdit(callback: EditListCallbackEvent): () => void {
		return this.listen(CUSTOM_DATA_EVENTS.RECIPE_LIST_EDITED, callback);
	}

	/**
	 * Dispatches the edit list event.
	 * @param {EditListPayload} payload - The data to send with the event.
	 */
	edit(payload: EditListPayload) {
		this.dispatch(CUSTOM_DATA_EVENTS.RECIPE_LIST_EDITED, payload);
	}

	/**
	 * Dispatches the fetch lists event.
	 * @param {FetchListsDetail} payload - The data to send with the event.
	 */
	fetch(payload: FetchListsPayload) {
		this.dispatch(CUSTOM_DATA_EVENTS.RECIPE_LIST_FETCHED, payload);
	}

	/**
	 * Listens for the fetch lists event.
	 * @param {FetchListsCallbackEvent} callback - Function to handle the event. Receives the full CustomEvent.
	 * @return {() => void} A function to unsubscribe the listener.
	 */
	onFetch(callback: FetchListsCallbackEvent): () => void {
		return this.listen(CUSTOM_DATA_EVENTS.RECIPE_LIST_FETCHED, callback);
	}

	/**
	 * Listens for the select list event.
	 * @param {SelectListCallbackEvent} callback - Function to handle the event. Receives the full CustomEvent.
	 * @return {() => void} A function to unsubscribe the listener.
	 */
	onSelect(callback: SelectListCallbackEvent): () => void {
		return this.listen(CUSTOM_DATA_EVENTS.RECIPE_LIST_SELECTED, callback);
	}

	/**
	 * Dispatches the select list event.
	 * @param {SelectListPayload} payload - The data to send with the event.
	 */
	select(payload: SelectListPayload) {
		this.dispatch(CUSTOM_DATA_EVENTS.RECIPE_LIST_SELECTED, payload);
	}
}
